import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { badConfig, badRating, CONFIG_ERR, SPEC_FIELDS, toNum } from "@/lib/pricing";
import { GlassFields, GridFields } from "@/components/quote/GlassFields";

export const SKETCH_OPTIONS = [
  ["single_hung", "Single hung"], ["double_hung", "Double hung"], ["casement", "Casement"], ["sliding", "Sliding window"],
  ["fixed", "Fixed / picture"], ["awning", "Awning"], ["entry_door", "Entry door"], ["sliding_patio", "Sliding patio door"],
  ["french", "French door"], ["interior_door", "Interior door"], ["garage", "Garage door"],
];

const cls = "h-9 w-full rounded-md border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-100 outline-none focus:border-amber-500/70";

export function TypeSpecsDialog({ type, onClose, onSaved, options }) {
  const [form, setForm] = useState({ sketch: "fixed", specs: {}, glass: {} });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (type) setForm({ sketch: type.sketch || "fixed", specs: { ...(type.specs || {}) }, glass: { ...(type.glass || {}) } });
  }, [type]);

  const save = async () => {
    if (badRating(form.glass.u_value) || badRating(form.glass.shgc)) return toast.error("U-factor and SHGC must be numbers 0 or greater");
    if (badConfig(form.specs.config)) return toast.error(CONFIG_ERR);
    setBusy(true);
    try {
      const g = form.glass;
      const glass = { glazing: g.glazing || "", glass_types: g.glass_types || [], gas: g.gas || "", thickness: g.thickness || "",
        u_value: toNum(g.u_value), shgc: toNum(g.shgc), grids: !!g.grids, grid_pattern: g.grid_pattern || "colonial",
        grid_rows: Math.min(12, Math.max(1, Math.round(toNum(g.grid_rows) || 2))), grid_cols: Math.min(12, Math.max(1, Math.round(toNum(g.grid_cols) || 2))),
        grid_type: g.grid_type || "SDL", grid_color: g.grid_color || "" };
      const { data } = await api.put(`/settings/types/${type.id}`, { ...form, glass });
      onSaved(data);
      toast.success(`Default specs saved for ${type.name}`);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!type} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="type-specs-dialog" className="max-h-[92vh] max-w-3xl overflow-y-auto border-slate-800 bg-[#F2F2F7]">
        <DialogHeader>
          <DialogTitle className="font-display text-slate-50">{type?.name} · default specs</DialogTitle>
          <DialogDescription className="text-slate-400">Pre-filled on every new line of this type. Editable per line.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 block">
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Elevation sketch</span>
            <select data-testid="type-specs-sketch-select" value={form.sketch} onChange={(e) => setForm({ ...form, sketch: e.target.value })} className={cls}>
              {SKETCH_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          {SPEC_FIELDS.map(([k, label]) => (
            <label key={k} className={`block ${k === "glass_details" || k === "product_name" ? "col-span-2" : ""}`}>
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
              <input data-testid={`type-specs-${k.replace(/_/g, "-")}-input`} value={form.specs[k] || ""} className={cls}
                onChange={(e) => setForm({ ...form, specs: { ...form.specs, [k]: e.target.value } })} />
            </label>
          ))}
          {["sliding", "sliding_patio"].includes(form.sketch) && (
            <label className="block">
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Default configuration (X sliding / O fixed)</span>
              <input data-testid="type-specs-config-input" value={form.specs.config || ""} placeholder="XO" className={`${cls} font-mono-num tracking-widest`}
                onChange={(e) => setForm({ ...form, specs: { ...form.specs, config: e.target.value.toUpperCase() } })} />
              {badConfig(form.specs.config) && <span data-testid="type-specs-config-error" className="mt-1 block text-xs text-red-400">{CONFIG_ERR}</span>}
            </label>
          )}
          <div className="col-span-2 border-t border-slate-800 pt-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Default glass & performance</p>
            <GlassFields row={form.glass} options={options} prefix="type-specs" suffix=""
              onChange={(p) => setForm((f) => ({ ...f, glass: { ...f.glass, ...p } }))} />
            <div className="mt-4">
              <GridFields row={{ ...form.glass, ext_color: form.specs.ext_color }} prefix="type-specs"
                onChange={(p) => setForm((f) => ({ ...f, glass: { ...f.glass, ...p } }))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <button data-testid="type-specs-cancel" onClick={onClose} className="rounded-full px-4 py-2 text-sm text-slate-400 hover:text-slate-100">Cancel</button>
          <button data-testid="type-specs-save-button" onClick={save} disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save specs
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
